import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import InputDocuments from './InputDocuments';
import LoadingStatic from './LoadingStatic';
import ShowBienes from './ShowBienes';
import Stepper from './Stepper';
import Pay from '../Pay/Pay';

import filesService from 'services/files.service';

const READY_STATUSES = new Set(['ready', 'paid', 'completed']);
const ERROR_STATUSES = new Set(['error']);
const POLL_INTERVAL_MS = 2000;
const MAX_STATUS_POLLS = 60;

const Assistant = () => {
    const [searchParams] = useSearchParams();
    const urlIdSub = searchParams.get('idSub');

    const [step, setStep] = useState(1);
    const [edictoFile, setEdictoFile] = useState(null);
    const [notaSimpleFile, setNotaSimpleFile] = useState(null);
    const [isWaiting, setIsWaiting] = useState(false);
    const [bienes, setBienes] = useState([]);
    const [selectedBienes, setSelectedBienes] = useState([]);
    const [analysisId, setAnalysisId] = useState('676f1cfd51bd7022d4d7b3c6');
    const [subastaId, setSubastaId] = useState(urlIdSub || '');
    const [fallback, setFallback] = useState(false);
    const [hasCargas, setHasCargas] = useState(false);
    const [hasUnclassifiedDocuments, setHasUnclassifiedDocuments] = useState(false);

    const handleSelectionChange = (selectedBienes) => {
        setSelectedBienes(selectedBienes);
    };

    const applySubastaData = (data) => {
        setBienes(data.answer.bienes_subastados);
        setHasCargas(data.answer.hasCargas);
        setHasUnclassifiedDocuments(Boolean(data.answer.hasUnclassifiedDocuments));
        setAnalysisId(data.analysisId);
        setStep(2);
    };

    const waitForDocumentCheck = async (currentAnalysisId) => {
        for (let attempt = 0; attempt < MAX_STATUS_POLLS; attempt++) {
            const response = await filesService.getAnalysisStatus(currentAnalysisId);
            if (response.status !== 200) {
                throw new Error('Could not retrieve analysis status');
            }

            const currentStatus = response.data.status;
            if (READY_STATUSES.has(currentStatus)) {
                return currentStatus;
            }

            if (ERROR_STATUSES.has(currentStatus)) {
                throw new Error('Analysis failed while checking BOE documents');
            }

            await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
        }

        throw new Error('Timed out while waiting for BOE document check');
    };

    const startWithId = async (subastaId) => {
        try {
            console.log('Subasta ID:', subastaId);
            setFallback(false);

            const response = await filesService.startWithId(subastaId);

            if (response.status === 200) {
                const data = response.data;
                const currentAnalysisId = data.analysisId;

                if (!currentAnalysisId) {
                    applySubastaData(data);
                    return;
                }

                const statusResponse = await filesService.getAnalysisStatus(currentAnalysisId);
                if (statusResponse.status !== 200) {
                    throw new Error('Could not retrieve analysis status');
                }

                const currentStatus = statusResponse.data.status;

                if (!READY_STATUSES.has(currentStatus)) {
                    await waitForDocumentCheck(currentAnalysisId);
                }

                const refreshedResponse = await filesService.startWithId(subastaId);
                if (refreshedResponse.status === 200) {
                    applySubastaData(refreshedResponse.data);
                } else {
                    applySubastaData(data);
                }
            } else {
                setStep(0.5);
                setFallback(true);
            }
        } catch (error) {
            console.error("Error starting with id:", error);
            setStep(0.5);
            setFallback(true);
        } finally {
            setIsWaiting(false);
        }
    }

    useEffect(() => {
        if (urlIdSub) {
            setSubastaId(urlIdSub);
            setIsWaiting(true);
            startWithId(urlIdSub);
        }
    }, [urlIdSub]);

    useEffect(() => {
        if (edictoFile) {
            handleEdictoUpload();
        }
    }, [edictoFile]);

    useEffect(() => {
        if (notaSimpleFile) {
            handleCargasUpload();
        }
    }, [notaSimpleFile]);

    const handleEdictoUpload = () => {
        if (!edictoFile) {
            alert("Please upload at least one document.");
            return;
        } else {
            setIsWaiting(true);
            uploadEdicto();
        }
    };

    const handleCargasUpload = () => {
        setIsWaiting(true);
        uploadCargas();
    };

    const uploadEdicto = async () => {
        const formData = new FormData();
        const edictoFileWithNewName = new File([edictoFile], `edicto_${edictoFile.name}`, { type: edictoFile.type });
        formData.append('files', edictoFileWithNewName);

        try {
            const response = await filesService.uploadEdicto(formData);

            if (response.status === 200) {
                const data = response.data;
                setBienes(data.answer.bienes_subastados);
                setAnalysisId(data.analysisId);
                setStep(2);
            }
        } catch (error) {
            console.error("Error uploading edicto:", error);
        } finally {
            setIsWaiting(false);
        }
    };

    const uploadCargas = async () => {
        const formData = new FormData();
        if (notaSimpleFile) {
            const notaSimpleFileWithNewName = new File([notaSimpleFile], `cargas_${notaSimpleFile.name}`, { type: notaSimpleFile.type });
            formData.append('files', notaSimpleFileWithNewName);
        }
        formData.append('analysisId', analysisId);
        formData.append('selectedBienes', JSON.stringify(selectedBienes));

        try {
            const response = await filesService.uploadCargas(formData);

            if (response.status === 200) {
                const data = response.data;
                if (data.analysisId) {
                    setAnalysisId(data.analysisId);
                }
                if (data.hasBalance === false) {
                    setStep(3);
                }
            }
        } catch (error) {
            console.error("Error uploading cargas:", error);
        } finally {
            // wait for 5 seconds
            setTimeout(() => {
                setIsWaiting(false);
            }, 5000);
        }
    };

    const payWithCredit = async () => {
        try {
            console.log('Analysis ID:', analysisId);
            const response = await filesService.useCredit(analysisId);

            if (response.status === 200) {
                const startedAnalysisId = response.data.analysisId || analysisId;
                setAnalysisId(startedAnalysisId);
                window.location.href = `/gracias?analysisId=${startedAnalysisId}`;
            }
        } catch (error) {
            console.error("Error using credit:", error);
        } finally {
            setIsWaiting(false);
        }
    }

    const handleSubastaIdChange = (e) => {
        console.log('Subasta ID:', e.target.value);
        setSubastaId(e.target.value);
    };

    const canStartAnalysis = hasCargas || hasUnclassifiedDocuments;

    const handleSubastaIdSubmit = (e) => {
        e.preventDefault();
        if (!subastaId) {
            return;
        } else {
            setIsWaiting(true);
            let idSub = subastaId.trim();

            // Robust extraction of idSub
            try {
                if (idSub.includes("idSub=")) {
                    const urlPart = idSub.split("idSub=")[1];
                    // Take everything until the next & or the end of the string
                    idSub = urlPart.split("&")[0];
                } else if (idSub.includes("subasta.php?id=")) { // Another potential format
                    const urlPart = idSub.split("id=")[1];
                    idSub = urlPart.split("&")[0];
                }
                
                // Clean up any remaining hash or junk
                idSub = idSub.split("#")[0];
            } catch (err) {
                console.error("Error parsing input string, using original value");
            }

            startWithId(idSub);
        }
    }

    return (
        <div className="h-full w-full flex flex-col justify-center items-center px-4">
            <Stepper step={step} />
            {isWaiting && <LoadingStatic />}
            {!isWaiting && step === 1 && (
                <>
                    <h2 className="text-4xl font-bold text-gray-700 dark:text-gray-300 p-4">Empecemos</h2>
                    <p className="text-xl text-gray-500 dark:text-gray-400 pt-4 pb-16">Copia el identificador de la subasta que quieres analizar. Puedes escoger cualquiera del <a href='https://subastas.boe.es' target='_blank' className='underline'>Portal de Subastas del BOE</a>.</p>
                    <form className="flex flex-col w-full items-center" onSubmit={handleSubastaIdSubmit}>
                        <div className="w-full md:w-6/12 flex justify-center items-center flex-col gap-4 px-2 md:px-12">
                            <input
                                type="text"
                                id="subastaId"
                                value={subastaId}
                                onChange={handleSubastaIdChange}
                                className="text-center block py-2.5 px-0 w-full text-xl text-gray-900 bg-transparent border-0 border-b-2 border-blueGray-300 appearance-none dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0 focus:border-blue-600 peer"
                                placeholder="SUB-JA-2025-XXXXXX"
                                required
                            />
                            <button
                                type="submit"
                                className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-2 px-4 ml-4 rounded-lg"
                            >
                                <i className='fas fa-arrow-right text-lg'></i>
                            </button>
                        </div>
                    </form>
                    <p
                        onClick={() => setStep(0.5)}
                        className="text-base cursor-pointer text-gray-500 dark:text-gray-400 my-8">
                        Prefiero subir los documentos manualmente
                    </p>
                </>
            )}
            {!isWaiting && step === 0.5 && (
                <>
                    <h2 className="text-4xl font-bold text-gray-700 dark:text-gray-300 p-4">
                        {fallback ?
                            'Parece que algo ha salido mal...'
                            :
                            `Empecemos`
                        }
                    </h2>
                    <p className="text-xl text-gray-500 dark:text-gray-400 pt-4 pb-16">
                        {fallback ? (
                            'No hemos podido encontrar la subasta con el indentificador introducido. Por favor, sube los documentos manualmente.'
                        ) : (
                            <>
                                Sube el edicto de la subasta. Si aun no lo tienes puedes obtenerlo en el{' '}
                                <a href='https://subastas.boe.es' target='_blank' className='underline'>
                                    Portal de Subastas del BOE
                                </a>.
                            </>
                        )}
                    </p>
                    <div className={`flex w-full md:w-2/3 h-full`}>
                        <InputDocuments type='edicto' setEdictoFile={setEdictoFile} setNotaSimpleFile={setNotaSimpleFile} edictoFile={edictoFile} notaSimpleFile={notaSimpleFile} />
                    </div>
                    <p
                        onClick={() => setStep(1)}
                        className="text-base cursor-pointer text-gray-500 dark:text-gray-400 my-8">
                        Prefiero introducir el identificador de la subasta
                    </p>
                </>
            )}
            {!isWaiting && step === 2 && (
                <div className={`flex flex-col md:flex-row w-full h-full`}>
                    <div className={`flex flex-col w-full ${canStartAnalysis ? '' : 'md:w-7/12'} h-full items-center justify-center`}>
                        <ShowBienes canStartAnalysis={canStartAnalysis} bienes={bienes} onSelectionChange={handleSelectionChange} subastaId={subastaId} handleCargasUpload={handleCargasUpload} />
                    </div>
                    {!canStartAnalysis && (
                        <div className="flex flex-col w-full md:w-5/12 items-start justify-start md:pl-4">
                            <div className='flex flex-col'>
                                <p className="text-xl text-gray-600 pt-2 pb-4 md:ml-2">No hemos podido encontrar la certificación de cargas en el Portal de Subastas del BOE. A continuación, adjunta las notas simples o un certificado de cargas para que podamos analizar qué cargas que no se cancelan tras la subasta</p>
                                <InputDocuments type='nota-simple' setEdictoFile={setEdictoFile} setNotaSimpleFile={setNotaSimpleFile} edictoFile={edictoFile} notaSimpleFile={notaSimpleFile} />
                            </div>
                        </div>
                    )}
                </div>
            )}
            {!isWaiting && step === 3 && (
                <div className={`flex w-full h-full`}>
                    <Pay analysisId={analysisId} payWithCredit={payWithCredit} />
                </div>
            )}
        </div>
    );
};
export default Assistant;
